import { request } from "./apiService";
import { getProgressionTitle, isStaffRole } from "../utils/graduation";

export const ALL_CRITERIA_REACHED_MESSAGE =
    "Continuez ainsi - vous remplissez tous les critères d'éligibilité à l'évaluation. Elle arrive très prochainement... restez prêt.";

export interface GraduationReason {
    code?: string;
    message?: string;
}

export interface GraduationCondition {
    key: string;
    label: string;
    description?: string;
    statusLabel?: "Validé" | "Non validé" | "Informatif" | "En attente" | "Promotion non lancée";
    currentValueLabel: string;
    targetValueLabel: string;
    progressPercent: number;
    isMet: boolean;
    remainingLabel?: string;
    isCritical?: boolean;
    isBlocking?: boolean;
    /** Texte affiché au-dessus de la jauge (ex. promo pas encore lancée). */
    gaugeCaption?: string;
    /** Critère inactif / en attente (jauge grise). */
    isPending?: boolean;
}

export interface GraduationIndicator {
    key:
        | "sessions90Days"
        | "dayWinRate"
        | "methodRate"
        | "funding"
        | "role";
    label: string;
    valueLabel: string;
    isValidated?: boolean;
}

export interface GraduationPathStep {
    key: string;
    title: string;
    description: string;
    status: "validated" | "in_progress" | "blocked";
    progressPercent: number;
}

export interface GraduationStatusResponse {
    pageTitle: string;
    pageSubtitle: string;
    isEligible: boolean;
    globalProgressPercent: number;
    globalProgressLabel?: string;
    reasons: GraduationReason[];
    highlightedAdviceMessage?: string;
    validatedByAdmin?: boolean;
    validatedBy?: {
        adminId: string;
        adminDisplayName?: string | null;
        validatedAt: string;
        updatedAt: string;
    } | null;
    currentRoleName?: string | null;
    nextRoleName?: string | null;
    previousRoleName?: string | null;
    /** Fenêtre de calcul (jours) issue des paramètres Progression. */
    lookbackDays: number;
    roleProgression?: {
        currentRoleName: string | null;
        currentRoleSince: string | null;
        tenures: Array<{
            roleName: string;
            startedAt: string;
            endedAt: string | null;
            durationDays: number;
        }>;
        promotions: Array<{
            id: string;
            fromRoleName: string | null;
            toRoleName: string;
            promotedAt: string;
            reason: string | null;
        }>;
    };
    conditions: GraduationCondition[];
    indicators: GraduationIndicator[];
    nextStep: {
        title: string;
        description: string;
        ctaLabel?: string;
        ctaRoute?: string;
    };
    path: {
        title: string;
        steps: GraduationPathStep[];
    };
    promotionExperience?: {
        status: "none" | "pending_launch" | "active";
        promotionId: string | null;
        promotionName: string | null;
        launchDate: string | null;
        tenureDays: number;
        message: string | null;
    };
}

interface GraduationApiCondition {
    key: string;
    label: string;
    completed: boolean;
    completionPercentage: number;
    currentValue: string | number | boolean;
    targetValue: string | number | boolean;
    comparator: "=" | ">=" | "<=" | ">" | "<";
}

interface GraduationApiResponse {
    eligible: boolean;
    reasons: string[];
    metrics: {
        sessionsLast90Days: number;
        methodRespect: number;
        dayWin: number;
        isFunded: boolean;
        lookbackDays?: number;
        minSessions?: number;
    };
    conditions: GraduationApiCondition[];
    coaching?: {
        key: string;
        level: "low" | "ok";
        messages: string[];
    }[];
    highlightedAdvice?: {
        key: string;
        level: "low" | "ok";
        message: string;
        completionPercentage: number;
    };
    nextStepAction?: {
        key: string;
        title: string;
        description: string;
        cta?: string;
        route?: string;
    };
    validatedByAdmin?: boolean;
    validatedBy?: {
        adminId: string;
        adminDisplayName?: string | null;
        validatedAt: string;
        updatedAt: string;
    } | null;
    currentRoleName?: string | null;
    nextRoleName?: string | null;
    previousRoleName?: string | null;
    roleProgression?: GraduationStatusResponse["roleProgression"];
    promotionExperience?: {
        status: "none" | "pending_launch" | "active";
        promotionId: string | null;
        promotionName: string | null;
        launchDate: string | null;
        tenureDays: number;
        message: string | null;
    };
}

const toLabelValue = (value: string | number | boolean) => {
    if (typeof value === "boolean") {
        return value ? "Oui" : "Non";
    }
    if (typeof value === "number") {
        return Number.isInteger(value) ? `${value}` : value.toFixed(2);
    }
    return value;
};

const formatPercentValue = (value: number) => {
    // Afficher jusqu a 2 decimales, sans zeros de fin.
    const fixed2 = value.toFixed(2);
    const trimmed = fixed2.replace(/\.?0+$/, "");
    return trimmed;
};

const buildPathSteps = (
    conditions: GraduationApiCondition[],
    lookbackDays: number
): GraduationPathStep[] => {
    const byKey = new Map(conditions.map((condition) => [condition.key, condition]));

    const roleCondition = byKey.get("role");
    const fundingCondition = byKey.get("funding");
    const sessionsCondition = byKey.get("sessions");
    const dayWinCondition = byKey.get("dayWin");

    const toStatus = (condition?: GraduationApiCondition): GraduationPathStep["status"] => {
        if (!condition) return "blocked";
        return condition.completed ? "validated" : "in_progress";
    };

    const sessionsTarget =
        typeof sessionsCondition?.targetValue === "number"
            ? sessionsCondition.targetValue
            : Number(sessionsCondition?.targetValue) || 0;
    const dayWinTarget =
        typeof dayWinCondition?.targetValue === "number"
            ? dayWinCondition.targetValue
            : Number(dayWinCondition?.targetValue) || 0;

    const steps: GraduationPathStep[] = [];

    if (roleCondition) {
        steps.push({
            key: "role",
            title: roleCondition.label || "Expérience Pro",
            description:
                "Ancienneté et expérience sur le parcours Pro.",
            status: toStatus(roleCondition),
            progressPercent: roleCondition.completionPercentage ?? 0,
        });
    }

    steps.push(
        {
            key: "funding",
            title: "Financement valide",
            description: "Le statut finance doit etre valide.",
            status: toStatus(fundingCondition),
            progressPercent: fundingCondition?.completionPercentage ?? 0,
        },
        {
            key: "sessions",
            title: `${sessionsTarget} sessions (${lookbackDays} jours)`,
            description: "Atteindre le volume de sessions requis.",
            status: toStatus(sessionsCondition),
            progressPercent: sessionsCondition?.completionPercentage ?? 0,
        },
        {
            key: "dayWin",
            title: `Day Win >= ${dayWinTarget}%`,
            description: "Maintenir le niveau de performance attendu.",
            status: toStatus(dayWinCondition),
            progressPercent: dayWinCondition?.completionPercentage ?? 0,
        },
        {
            key: "final",
            title: "Validation finale",
            description:
                "Validation globale de l'éligibilité aux critères d'apprentissage Objectif Trading.",
            status:
                (!roleCondition || roleCondition.completed) &&
                fundingCondition?.completed &&
                sessionsCondition?.completed &&
                dayWinCondition?.completed
                    ? "validated"
                    : "blocked",
            progressPercent:
                (!roleCondition || roleCondition.completed) &&
                fundingCondition?.completed &&
                sessionsCondition?.completed &&
                dayWinCondition?.completed
                    ? 100
                    : 0,
        }
    );

    return steps;
};

const mapGraduationResponse = (
    response: GraduationApiResponse
): GraduationStatusResponse => {
    const isValidatedByAdmin = Boolean(response.validatedByAdmin);
    const isStaffAccount = isStaffRole(response.currentRoleName);
    const lookbackDays = response.metrics.lookbackDays ?? 90;

    const avgProgress =
        response.conditions.length > 0
            ? response.conditions.reduce(
                  (sum, condition) => sum + condition.completionPercentage,
                  0
              ) / response.conditions.length
            : 0;

    const weakestNotCompleted = response.conditions
        .filter((condition) => !condition.completed)
        .sort((a, b) => a.completionPercentage - b.completionPercentage)[0];

    const conditionsOrder: Record<string, number> = {
        sessions: 0,
        dayWin: 1,
        funding: 2,
        tenure: 3,
        role: 4,
        method: 5,
    };

    const sortedConditions = [...response.conditions].sort(
        (a, b) =>
            (conditionsOrder[a.key] ?? Number.MAX_SAFE_INTEGER) -
            (conditionsOrder[b.key] ?? Number.MAX_SAFE_INTEGER),
    );

    const mappedConditions: GraduationCondition[] = sortedConditions.map((condition) => {
        const conditionStatusLabel: GraduationCondition["statusLabel"] =
            isValidatedByAdmin || isStaffAccount
                ? "Informatif"
                : condition.completed
                  ? "Validé"
                  : "Non validé";
        const common = {
            key: condition.key,
            label:
                condition.key === "role" || condition.key === "tenure"
                    ? "Expérience"
                    : condition.key === "sessions"
                      ? "Régularité"
                      : condition.key === "dayWin"
                        ? "Rentabilité"
                        : condition.key === "funding"
                          ? "Financement"
                          : condition.label,
            description:
                condition.key === "sessions"
                    ? `Volume de sessions de trading requis (1 par jour, ${lookbackDays} derniers jours)`
                    : condition.key === "dayWin"
                      ? `Pourcentage de journée gagnante sur les ${lookbackDays} derniers jours`
                      : condition.key === "funding"
                        ? "Statut de trader financé"
                        : condition.key === "tenure"
                          ? "Jours depuis le lancement de la promotion"
                          : condition.key === "role"
                            ? "Progression sur 6 mois depuis le passage Pro"
                            : undefined,
            progressPercent: condition.completionPercentage,
            isMet: condition.completed,
            isCritical: condition.key === "funding" || condition.key === "role",
            isBlocking: !isStaffAccount && condition.key !== "method",
            statusLabel: conditionStatusLabel,
        };

        // Formats UX (aucune logique metier : on ne fait que presenter les valeurs backend).
        if (condition.key === "sessions") {
            const current =
                typeof condition.currentValue === "number"
                    ? condition.currentValue
                    : Number(condition.currentValue);
            const target =
                typeof condition.targetValue === "number"
                    ? condition.targetValue
                    : Number(condition.targetValue);
            const remaining =
                !condition.completed ? Math.max(0, target - current) : undefined;

            return {
                ...common,
                currentValueLabel:
                    typeof current === "number" && typeof target === "number"
                        ? `${current} / ${target} sessions`
                        : `${toLabelValue(condition.currentValue)}`,
                targetValueLabel:
                    typeof target === "number"
                        ? `Avoir ${target} sessions sur les ${lookbackDays} derniers jours`
                        : `${toLabelValue(condition.targetValue)}`,
                remainingLabel:
                    typeof remaining === "number" && remaining > 0
                        ? `${remaining} sessions nécessaires`
                        : undefined,
            };
        }

        if (condition.key === "dayWin") {
            const current =
                typeof condition.currentValue === "number"
                    ? condition.currentValue
                    : Number(condition.currentValue);
            const target =
                typeof condition.targetValue === "number"
                    ? condition.targetValue
                    : Number(condition.targetValue);
            const remaining =
                !condition.completed ? Math.max(0, target - current) : undefined;

            return {
                ...common,
                currentValueLabel:
                    typeof current === "number" && typeof target === "number"
                        ? `${formatPercentValue(current)}% / ${formatPercentValue(target)}%`
                        : `${toLabelValue(condition.currentValue)}`,
                targetValueLabel:
                    typeof target === "number"
                        ? `Avoir ${formatPercentValue(target)}% de tes journées gagnantes sur les ${lookbackDays} derniers jours`
                        : `${toLabelValue(condition.targetValue)}`,
                remainingLabel:
                    typeof remaining === "number" && remaining > 0
                        ? `${formatPercentValue(remaining)}% nécessaires`
                        : undefined,
            };
        }

        if (condition.key === "funding") {
            const current = Boolean(condition.currentValue);
            return {
                ...common,
                currentValueLabel: current ? "Financé" : "Non financé",
                targetValueLabel: "Être actuellement financé par une prop firm",
                remainingLabel: undefined,
            };
        }

        if (condition.key === "tenure") {
            const current =
                typeof condition.currentValue === "number"
                    ? condition.currentValue
                    : Number(condition.currentValue);
            const target =
                typeof condition.targetValue === "number"
                    ? condition.targetValue
                    : Number(condition.targetValue);
            const remaining = !condition.completed
                ? Math.max(0, target - current)
                : undefined;

            return {
                ...common,
                currentValueLabel:
                    typeof current === "number" && typeof target === "number"
                        ? `${current} / ${target} jours`
                        : `${toLabelValue(condition.currentValue)}`,
                targetValueLabel:
                    typeof target === "number"
                        ? `Atteindre ${target} jours depuis le lancement de la promotion`
                        : `${toLabelValue(condition.targetValue)}`,
                remainingLabel:
                    typeof remaining === "number" && remaining > 0
                        ? `${remaining} jours nécessaires`
                        : undefined,
            };
        }

        if (condition.key === "role") {
            const current =
                typeof condition.currentValue === "number"
                    ? condition.currentValue
                    : Number(condition.currentValue);
            const target =
                typeof condition.targetValue === "number"
                    ? condition.targetValue
                    : 100;
            const remaining =
                !condition.completed && Number.isFinite(current)
                    ? Math.max(0, target - current)
                    : undefined;

            return {
                ...common,
                currentValueLabel: "6 mois actifs en Pro",
                targetValueLabel:
                    "Atteindre 6 mois en Pro",
                remainingLabel:
                    typeof remaining === "number" && remaining > 0
                        ? `${formatPercentValue(remaining)}% restants`
                        : undefined,
            };
        }

        return {
            ...common,
            currentValueLabel: `${toLabelValue(condition.currentValue)}`,
            targetValueLabel: `${toLabelValue(condition.targetValue)}`,
            remainingLabel: undefined,
        };
    });

    const methodMinimumThreshold = 80;
    const methodProgressPercent = Math.max(
        0,
        Math.min(100, (response.metrics.methodRespect / methodMinimumThreshold) * 100),
    );

    mappedConditions.push({
        key: "method",
        label: "Méthode",
        description: "Critère informatif",
        currentValueLabel: `${formatPercentValue(response.metrics.methodRespect)}%`,
        targetValueLabel: `Avoir une méthode d'exécution à au moins ${methodMinimumThreshold}%`,
        progressPercent: methodProgressPercent,
        isMet: false,
        isCritical: false,
        isBlocking: false,
        statusLabel: "Informatif",
    });

    const isPendingLaunch =
        response.promotionExperience?.status === "pending_launch";
    const conditions = isPendingLaunch
        ? mappedConditions.map((condition) => ({
              ...condition,
              progressPercent: 0,
              isMet: false,
              isPending: true,
              remainingLabel: undefined,
              gaugeCaption: "Promotion non lancée",
              statusLabel: "Promotion non lancée" as const,
              ...(condition.key === "tenure"
                  ? {
                        currentValueLabel: condition.currentValueLabel.replace(
                            /^\d+\s*\//,
                            "0 /"
                        ),
                    }
                  : {}),
          }))
        : mappedConditions;

    return {
        pageTitle: getProgressionTitle(response.currentRoleName),
        pageSubtitle: isStaffAccount
            ? "Hors parcours trader : indicateurs affichés à titre informatif."
            : "",
        isEligible: response.eligible,
        globalProgressPercent: isPendingLaunch
            ? 0
            : Number(avgProgress.toFixed(2)),
        globalProgressLabel: undefined,
        lookbackDays,
        reasons: response.reasons.map((reason) => ({ message: reason })),
        validatedByAdmin: Boolean(response.validatedByAdmin),
        validatedBy: response.validatedBy ?? null,
        currentRoleName: response.currentRoleName ?? null,
        nextRoleName: response.nextRoleName ?? null,
        previousRoleName: response.previousRoleName ?? null,
        roleProgression: response.roleProgression ?? undefined,
        promotionExperience: response.promotionExperience,
        highlightedAdviceMessage:
            isPendingLaunch
                ? response.promotionExperience?.message ?? undefined
                : response.highlightedAdvice?.message,
        conditions,
        indicators: [
            {
                key: "sessions90Days",
                label: `Nbs de sessions sur ${lookbackDays} jours`,
                valueLabel: `${response.metrics.sessionsLast90Days}`,
            },
            {
                key: "dayWinRate",
                label: "Day Win %",
                valueLabel: `${response.metrics.dayWin}%`,
            },
            {
                key: "methodRate",
                label: "Méthode %",
                valueLabel: `${response.metrics.methodRespect}%`,
            },
            {
                key: "funding",
                label: "Financé par une prop firm",
                valueLabel: response.metrics.isFunded
                    ? "Financé"
                    : "Non financé",
                isValidated: response.metrics.isFunded,
            },
        ],
        nextStep: {
            title: response.nextStepAction?.title ?? "Tâche recommandée",
            description: response.nextStepAction?.description ??
                (weakestNotCompleted
                    ? weakestNotCompleted.label
                    : ALL_CRITERIA_REACHED_MESSAGE),
            ctaLabel: response.nextStepAction?.cta,
            ctaRoute: response.nextStepAction?.route,
        },
        path: {
            title: "Parcours de validation",
            steps: buildPathSteps(response.conditions, lookbackDays),
        },
    };
};

export const getGraduationStatus = async () => {
    const response = await request<GraduationApiResponse>("/graduation/");
    return response ? mapGraduationResponse(response) : undefined;
};

export const getGraduationStatusByUserId = async (userId: string) => {
    const response = await request<GraduationApiResponse>(`/graduation/${userId}`);
    return response ? mapGraduationResponse(response) : undefined;
};
